import React, { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import { Button, Menu, MenuItem, Tooltip } from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import MusicNoteIcon from "@material-ui/icons/MusicNote";
import TableRowProfile from "../../util/TableRowProfile";
import image from "../../../helper/image";
import ActMediaModal from "./ActMediaModal";
import "./ActRosterTable.css";
import { getActGenreNames } from "./genres";
import "../../booking/BookingDetails.css";

const legalEntityLabels = {
    0: "Not Set",
    1: "Sole Trader",
    2: "Partnership",
    3: "Company",
    4: "Trust",
    5: "Hobbyist"
};

const vibesColorMap = {
    "Chilled": { bg: "#e0f2f1", color: "#00695c" },
    "Relaxed": { bg: "#e0f2f1", color: "#00695c" },
    "Acoustic": { bg: "#e8f5e9", color: "#2e7d32" },
    "Easy Listening": { bg: "#e8f5e9", color: "#2e7d32" },
    "Middle of the Road": { bg: "#fff8e1", color: "#f57c00" },
    "Upbeat": { bg: "#fff3e0", color: "#e65100" },
    "Party": { bg: "#fce4ec", color: "#c2185b" },
    "High Energy": { bg: "#fce4ec", color: "#c2185b" },
    "Dance": { bg: "#f3e5f5", color: "#7b1fa2" },
    "Club": { bg: "#f3e5f5", color: "#7b1fa2" },
    "Rock": { bg: "#ffebee", color: "#c62828" },
    "Alternative": { bg: "#ffebee", color: "#c62828" },
    "Jazz": { bg: "#e8eaf6", color: "#3949ab" },
    "Blues": { bg: "#e3f2fd", color: "#1565c0" },
    "Soul": { bg: "#ede7f6", color: "#5e35b1" },
    "R&B": { bg: "#ede7f6", color: "#5e35b1" },
    "Country": { bg: "#efebe9", color: "#5d4037" },
    "Folk": { bg: "#efebe9", color: "#5d4037" },
    "Classical": { bg: "#eceff1", color: "#455a64" },
    "Elegant": { bg: "#eceff1", color: "#455a64" },
    "Pop": { bg: "#e1f5fe", color: "#0277bd" },
    "Top 40": { bg: "#e1f5fe", color: "#0277bd" },
    "Covers": { bg: "#f1f8e9", color: "#558b2f" },
    "Originals": { bg: "#fff9c4", color: "#9e9d24" },
    "default": { bg: "#f5f5f5", color: "#616161" }
};

const getVibeColor = (vibe) => {
    return vibesColorMap[vibe] || vibesColorMap["default"];
};

const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    return trimmed.startsWith('http://') || trimmed.startsWith('https://');
};

@inject("profileStore")
@withRouter
@observer
class ActRosterTable extends Component {
    state = {
        actionAnchor: [],
        vibesPopover: [],
        mediaModalOpen: false,
        mediaModalUrl: "",
        mediaModalTitle: "",
        mediaModalType: ""
    };

    setActionAnchor = (element, id) => {
        var tempAnchor = [];
        tempAnchor[id] = element;
        this.setState({ actionAnchor: tempAnchor });
    };

    unsetActionAnchor = () => {
        this.setState({ actionAnchor: [] });
    };

    toggleVibesPopover = (id) => {
        const { vibesPopover } = this.state;
        const newPopover = [...vibesPopover];
        newPopover[id] = !newPopover[id];
        this.setState({ vibesPopover: newPopover });
    };

    closeVibesPopover = (id) => {
        const { vibesPopover } = this.state;
        const newPopover = [...vibesPopover];
        newPopover[id] = false;
        this.setState({ vibesPopover: newPopover });
    };

    parseVibes = (vibesName) => {
        if (!vibesName) return [];
        return vibesName.split(",").map(v => v.trim()).filter(v => v);
    };

    getLegalEntityLabel = (legalEntity) => {
        return legalEntityLabels[legalEntity] || "Not Set";
    };

    openMediaModal = (url, actName, type) => {
        const title = type === "video" 
            ? `${actName} - Video` 
            : `${actName} - Music`;
        this.setState({
            mediaModalOpen: true,
            mediaModalUrl: url,
            mediaModalTitle: title,
            mediaModalType: type
        });
    };

    closeMediaModal = () => {
        this.setState({
            mediaModalOpen: false,
            mediaModalUrl: "",
            mediaModalTitle: "",
            mediaModalType: ""
        });
    };

    render() {
        const { 
            acts, 
            onCreateBooking, 
            onViewContact, 
            onSendMessage, 
            onUpdateInvoice, 
            onViewSuper, 
            onRemove 
        } = this.props;
        const { actionAnchor, vibesPopover } = this.state;

        if (!acts || acts.length === 0) {
            return (
                <div className="act-roster-empty">
                    <p>No acts found in your roster.</p>
                </div>
            );
        }

        return (
            <div className="act-roster-table-container">
                <table className="act-roster-table">
                    <thead>
                        <tr>
                            <th style={{ minWidth: "140px", width: "14%" }}>Act</th>
                            <th style={{ minWidth: "100px", width: "13%" }}>Type & Lineup</th>
                            <th style={{ minWidth: "120px", width: "15%" }}>Vibes/Genres</th>
                            <th style={{ minWidth: "130px", width: "18%" }}>Super/Invoicing</th>
                            <th style={{ minWidth: "140px", width: "20%" }}>Contact</th>
                            <th style={{ minWidth: "80px", width: "10%" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {acts.map((data, index) => {
                            const act = data.act;
                            if (!act) return null;

                            const vibes = this.parseVibes(act.vibesName);
                            const genres = getActGenreNames(act, this.props.genreCatalog);
                            const allTags = [...vibes.map(v => ({ type: 'vibe', value: v })), ...genres.map(g => ({ type: 'genre', value: g }))];
                            const visibleTags = allTags.slice(0, 3);
                            const remainingTagsCount = allTags.length - 3;
                            const legalEntityLabel = this.getLegalEntityLabel(act?.actAdmin?.legalEntity);
                            const isSuperComplete = act.isSuperCompleted;
                            const isPlayerInvoicesEnabled = data.invoiceType === 1;
                            const isLegalEntitySet = act?.actAdmin?.legalEntity && act?.actAdmin?.legalEntity !== 0;
                            const hasVideoUrl = isValidUrl(act.videoUrl);
                            const hasMusicUrl = isValidUrl(act.musicUrl);

                            return (
                                <tr key={data.id || index}>
                                    <td>
                                        <div className="act-cell">
                                            <TableRowProfile 
                                                profileId={act.id} 
                                                name={act.actName} 
                                                avatarUrl={image.getProfileImage(act)} 
                                            />
                                            <div className="act-cell-bottom">
                                                <div className="act-owner">
                                                    by {act.myMusician?.fullName || "Unknown"}
                                                </div>
                                                <div className="act-media-buttons">
                                                    <Tooltip title={hasVideoUrl ? "Watch Video" : "No video available"}>
                                                        <span>
                                                            <button
                                                                className={`media-btn video-btn ${hasVideoUrl ? 'enabled' : 'disabled'}`}
                                                                disabled={!hasVideoUrl}
                                                                onClick={() => hasVideoUrl && this.openMediaModal(act.videoUrl, act.actName, "video")}
                                                            >
                                                                <PlayArrowIcon style={{ fontSize: 16 }} />
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={hasMusicUrl ? "Listen to Music" : "No music available"}>
                                                        <span>
                                                            <button
                                                                className={`media-btn music-btn ${hasMusicUrl ? 'enabled' : 'disabled'}`}
                                                                disabled={!hasMusicUrl}
                                                                onClick={() => hasMusicUrl && this.openMediaModal(act.musicUrl, act.actName, "music")}
                                                            >
                                                                <MusicNoteIcon style={{ fontSize: 16 }} />
                                                            </button>
                                                        </span>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="chip-group">
                                            {act.actTypeName && (
                                                <span className="chip chip-type">{act.actTypeName}</span>
                                            )}
                                            {act.lineupCountName && (
                                                <span className="chip chip-lineup">{act.lineupCountName}</span>
                                            )}
                                            {!act.actTypeName && !act.lineupCountName && (
                                                <span className="chip chip-empty">-</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="chip-group vibes-cell">
                                            {visibleTags.map((tag, i) => {
                                                if (tag.type === 'vibe') {
                                                    const vibeColor = getVibeColor(tag.value);
                                                    return (
                                                        <span 
                                                            key={i} 
                                                            className="chip"
                                                            style={{ background: vibeColor.bg, color: vibeColor.color }}
                                                        >
                                                            {tag.value}
                                                        </span>
                                                    );
                                                } else {
                                                    return (
                                                        <span 
                                                            key={i} 
                                                            className="chip chip-genre"
                                                        >
                                                            {tag.value}
                                                        </span>
                                                    );
                                                }
                                            })}
                                            {remainingTagsCount > 0 && (
                                                <Fragment>
                                                    <span 
                                                        className="chip chip-more"
                                                        onClick={() => this.toggleVibesPopover(data.id)}
                                                    >
                                                        +{remainingTagsCount} more
                                                    </span>
                                                    {vibesPopover[data.id] && (
                                                        <Fragment>
                                                            <div 
                                                                className="vibes-popover-overlay" 
                                                                onClick={() => this.closeVibesPopover(data.id)} 
                                                            />
                                                            <div className="vibes-popover">
                                                                {vibes.length > 0 && (
                                                                    <>
                                                                        <div className="vibes-popover-header">Vibes</div>
                                                                        <div className="vibes-popover-list">
                                                                            {vibes.map((vibe, i) => {
                                                                                const vibeColor = getVibeColor(vibe);
                                                                                return (
                                                                                    <span 
                                                                                        key={i} 
                                                                                        className="chip"
                                                                                        style={{ background: vibeColor.bg, color: vibeColor.color }}
                                                                                    >
                                                                                        {vibe}
                                                                                    </span>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {genres.length > 0 && (
                                                                    <>
                                                                        <div className="vibes-popover-header" style={{ borderTop: vibes.length > 0 ? '1px solid #eee' : 'none' }}>Genres</div>
                                                                        <div className="vibes-popover-list">
                                                                            {genres.map((genre, i) => (
                                                                                <span key={i} className="chip chip-genre">{genre}</span>
                                                                            ))}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </Fragment>
                                                    )}
                                                </Fragment>
                                            )}
                                            {allTags.length === 0 && (
                                                <span className="chip chip-empty">-</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="status-badges">
                                            <Tooltip title={`Legal Entity: ${legalEntityLabel}`}>
                                                <span className={`status-badge ${isLegalEntitySet ? "complete" : "not-set"}`}>
                                                    <i className="fa fa-building"></i>
                                                    {legalEntityLabel}
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={isSuperComplete ? "Superannuation details have been submitted" : "Superannuation details not yet submitted"}>
                                                <span className={`status-badge ${isSuperComplete ? "complete" : "not-set"}`}>
                                                    <i className={`fa ${isSuperComplete ? "fa-check-circle" : "fa-minus-circle"}`}></i>
                                                    {isSuperComplete ? "Super Details Added" : "Super Details Not Set"}
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={isPlayerInvoicesEnabled ? "You will receive invoices from players of this act" : "Invoice setting not configured"}>
                                                <span className={`status-badge ${isPlayerInvoicesEnabled ? "complete" : "not-set"}`}>
                                                    <i className={`fa ${isPlayerInvoicesEnabled ? "fa-check-circle" : "fa-minus-circle"}`}></i>
                                                    {isPlayerInvoicesEnabled ? "Player Invoices Enabled" : "Player Invoices Not Set"}
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="contact-chips">
                                            {(act.contactPhone || act.actAdmin?.phone) && (
                                                <a href={`tel:${act.contactPhone || act.actAdmin?.phone}`} className="contact-chip phone">
                                                    <i className="fa fa-phone"></i>
                                                    {act.contactPhone || act.actAdmin?.phone}
                                                </a>
                                            )}
                                            {(act.contactEmail || act.actAdmin?.email) && (
                                                <a href={`mailto:${act.contactEmail || act.actAdmin?.email}`} className="contact-chip email">
                                                    <i className="fa fa-envelope"></i>
                                                    {act.contactEmail || act.actAdmin?.email}
                                                </a>
                                            )}
                                            {!act.contactPhone && !act.actAdmin?.phone && !act.contactEmail && !act.actAdmin?.email && (
                                                <span className="contact-chip empty">No contact info</span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="gig-actions-dropdown">
                                            <Button 
                                                className="gig-actions-dropdown-btn"
                                                onClick={(e) => this.setActionAnchor(e.currentTarget, data.id)}
                                            >
                                                Actions <ArrowDropDownIcon />
                                            </Button>
                                            <Menu
                                                open={actionAnchor[data.id] != null}
                                                onClose={this.unsetActionAnchor}
                                                anchorEl={actionAnchor[data.id]}
                                                getContentAnchorEl={null}
                                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                            >
                                                <MenuItem 
                                                    onClick={() => { this.unsetActionAnchor(); onCreateBooking(act?.id); }}
                                                    className="menu-item-primary"
                                                >
                                                    <i className="fa fa-plus menu-icon"></i>
                                                    Create Booking
                                                </MenuItem>
                                                <div className="menu-divider" />
                                                <MenuItem onClick={() => { this.unsetActionAnchor(); onViewContact(act); }}>
                                                    <i className="fa fa-phone menu-icon"></i>
                                                    View Contact Details
                                                </MenuItem>
                                                <MenuItem onClick={() => { this.unsetActionAnchor(); onSendMessage(act); }}>
                                                    <i className="fa fa-envelope menu-icon"></i>
                                                    Send Message
                                                </MenuItem>
                                                <div className="menu-divider" />
                                                <MenuItem 
                                                    onClick={() => { this.unsetActionAnchor(); this.props.onViewDocuments && this.props.onViewDocuments(act); }}
                                                    className="menu-item-new"
                                                >
                                                    <i className="fa fa-folder-open menu-icon"></i>
                                                    View Documents
                                                </MenuItem>
                                                <MenuItem 
                                                    onClick={() => { this.unsetActionAnchor(); this.props.onDownloadImages && this.props.onDownloadImages(act); }}
                                                    className="menu-item-new"
                                                >
                                                    <i className="fa fa-images menu-icon"></i>
                                                    Download Images
                                                </MenuItem>
                                                <div className="menu-divider" />
                                                <MenuItem onClick={() => { this.unsetActionAnchor(); onUpdateInvoice(data); }}>
                                                    <i className="fa fa-file-invoice menu-icon"></i>
                                                    Update Invoice Setting
                                                </MenuItem>
                                                <MenuItem onClick={() => { this.unsetActionAnchor(); onViewSuper(act); }}>
                                                    <i className="fa fa-shield-halved menu-icon"></i>
                                                    View Super Account Details
                                                </MenuItem>
                                                <div className="menu-divider" />
                                                <MenuItem 
                                                    onClick={() => { this.unsetActionAnchor(); onRemove(data); }}
                                                    className="menu-item-danger"
                                                >
                                                    <i className="fa fa-trash menu-icon"></i>
                                                    Remove
                                                </MenuItem>
                                            </Menu>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                <ActMediaModal
                    open={this.state.mediaModalOpen}
                    onClose={this.closeMediaModal}
                    url={this.state.mediaModalUrl}
                    title={this.state.mediaModalTitle}
                    type={this.state.mediaModalType}
                />
            </div>
        );
    }
}

export default ActRosterTable;
